import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Textarea } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { useSalesUsers } from '../components/DealerSales';
import { ProductMultiPicker } from '../components/ProductMultiPicker';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { CommissionRule, CommissionStatement, CommissionStatementDetail } from '../lib/types';
import { percentToBps } from './pricing';
import { useCatalogRefs } from './products';
import { CheckList } from './promotions';

const statusTone = { draft: 'amber', approved: 'pine', paid: 'moss' } as const;

/** Önceki ay (YYYY-MM): dönem hesabı genelde kapanan ay için yapılır. */
function previousMonth(): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

/**
 * Satış temsilcisi komisyonları (F7-09, ADR-112): kurallar ve aylık dökümler. Hesap net tahsilata göredir; sonraki
 * dönemler iade/iptal/geç tahsilatı düzeltme olarak yazar. Ödeme sistem dışında yapılır, döküm "ödendi" işaretlenir.
 */
export function CommissionsPage() {
  const t = useT();
  const { can, dealerScoped } = usePermissions();
  const manage = can('b2b.commission.manage') && !dealerScoped;
  return (
    <PageFrame helpKey="page.sales_commissions">
      <RulesSection manage={manage} />
      <PeriodSection manage={manage} />
      {dealerScoped && <p className="text-sm text-ink-3">{t('admin.commissions.ownOnly')}</p>}
    </PageFrame>
  );
}

function RulesSection({ manage }: { manage: boolean }) {
  const t = useT();
  const rules = useQuery({
    queryKey: ['commission-rules'],
    queryFn: () => api.get<CommissionRule[]>('/v1/admin/commission-rules'),
  });
  const [editing, setEditing] = useState<CommissionRule | 'new' | null>(null);
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/commission-rules/${id}`), {
    invalidate: [['commission-rules']],
    success: t('admin.commissions.ruleDeleted'),
  });
  const scope = (r: CommissionRule) =>
    !r.productIds.length && !r.categoryIds.length
      ? t('admin.commissions.allProducts')
      : [
          r.categoryIds.length && t('admin.commissions.categoryCount', { count: r.categoryIds.length }),
          r.productIds.length && t('admin.commissions.productCount', { count: r.productIds.length }),
        ]
          .filter(Boolean)
          .join(', ');
  return (
    <Section
      title={t('admin.commissions.rules')}
      description={t('admin.commissions.rulesLead')}
      action={
        <span className="flex items-center gap-1">
          {manage && (
            <Button variant="primary" onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.commissions.newRule')}
            </Button>
          )}
          <HelpTip k="sales_commissions.rules" />
        </span>
      }
    >
      {rules.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !rules.data?.length ? (
        <EmptyState title={t('admin.commissions.noRules')} description={t('admin.commissions.noRulesLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[40rem] text-sm" aria-label={t('admin.commissions.rules')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.commissions.ruleName')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.commissions.rep')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.commissions.scope')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.commissions.rate')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.commissions.validity')}</th>
                {manage && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {rules.data.map((r) => (
                <tr key={r.id} className="border-t border-line">
                  <td className="px-3 py-2">
                    {r.name}
                    {!r.active && (
                      <Badge tone="slate" className="ms-2">
                        {t('admin.commissions.inactive')}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.repName ?? t('admin.commissions.allReps')}</td>
                  <td className="px-3 py-2 text-ink-2">{scope(r)}</td>
                  <td className="px-3 py-2 text-end tabular">{bpsLabel(r.rateBps)}</td>
                  <td className="px-3 py-2 text-ink-2">
                    {r.validFrom || r.validTo
                      ? `${r.validFrom ?? '…'} – ${r.validTo ?? '…'}`
                      : t('admin.commissions.always')}
                  </td>
                  {manage && (
                    <td className="px-3 py-2 text-end">
                      <span className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                          {t('common.edit')}
                        </Button>
                        <ConfirmButton
                          message={t('admin.commissions.confirmDeleteRule', { name: r.name })}
                          onConfirm={() => remove.mutateAsync(r.id)}
                        >
                          {t('common.delete')}
                        </ConfirmButton>
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <RuleDialog rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Section>
  );
}

function RuleDialog({ rule, onClose }: { rule: CommissionRule | null; onClose: () => void }) {
  const t = useT();
  const users = useSalesUsers();
  const { categories } = useCatalogRefs();
  const [name, setName] = useState(rule?.name ?? '');
  const [rep, setRep] = useState(rule?.repUserId ?? '');
  const [rate, setRate] = useState(rule ? bpsLabel(rule.rateBps).replace('%', '') : '');
  const [categoryIds, setCategoryIds] = useState<string[]>(rule?.categoryIds ?? []);
  const [productIds, setProductIds] = useState<string[]>(rule?.productIds ?? []);
  const [active, setActive] = useState(rule?.active ?? true);
  const [from, setFrom] = useState(rule?.validFrom ?? '');
  const [to, setTo] = useState(rule?.validTo ?? '');
  const rateBps = percentToBps(rate);
  const save = useApiMutation(
    () => {
      const body = {
        name: name.trim(),
        repUserId: rep || null,
        rateBps: rateBps ?? -1,
        categoryIds,
        productIds,
        active,
        validFrom: from || null,
        validTo: to || null,
      };
      return rule
        ? api.patch(`/v1/admin/commission-rules/${rule.id}`, body, { ifMatch: rule.version })
        : api.post('/v1/admin/commission-rules', body);
    },
    {
      invalidate: [['commission-rules']],
      success: t('admin.commissions.ruleSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const fe = (f: string) => fieldError(save.error, f);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      variant="sheet"
      title={rule ? t('admin.commissions.editRule', { name: rule.name }) : t('admin.commissions.newRule')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={name.trim().length < 2 || rateBps === null}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.commissions.saveRule')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.commissions.ruleName')} required error={fe('name')}>
          {(p) => <Input {...p} maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('admin.commissions.rate')}
            required
            description={t('admin.commissions.rateHint')}
            help={<HelpTip k="sales_commissions.rate" />}
            error={
              fe('rateBps') ?? (rate && rateBps === null ? t('admin.commissions.rateInvalid') : undefined)
            }
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                className="w-32"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('admin.commissions.rep')}
            help={<HelpTip k="sales_commissions.rule_rep" />}
            error={fe('repUserId')}
          >
            {(p) => (
              <Select {...p} value={rep} onChange={(e) => setRep(e.target.value)}>
                <option value="">{t('admin.commissions.allReps')}</option>
                {users.data?.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.commissions.validFrom')}
            optionalLabel={t('common.optional')}
            error={fe('validFrom')}
          >
            {(p) => <Input {...p} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />}
          </Field>
          <Field
            label={t('admin.commissions.validTo')}
            optionalLabel={t('common.optional')}
            error={fe('validTo')}
          >
            {(p) => <Input {...p} type="date" value={to} onChange={(e) => setTo(e.target.value)} />}
          </Field>
        </div>
        <CheckList
          legend={t('admin.commissions.categories')}
          help={<HelpTip k="sales_commissions.rule_scope" />}
          items={categories.map((c) => ({ id: c.id, name: c.name }))}
          value={categoryIds}
          onChange={setCategoryIds}
        />
        <ProductMultiPicker
          label={t('admin.commissions.products')}
          value={productIds}
          onChange={setProductIds}
          error={fe('productIds')}
          max={500}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-[var(--zt-pine)]"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          {t('admin.commissions.activeLabel')}
        </label>
      </div>
    </Dialog>
  );
}

function PeriodSection({ manage }: { manage: boolean }) {
  const t = useT();
  const money = useMoney();
  const [period, setPeriod] = useState(previousMonth());
  const [open, setOpen] = useState<string | null>(null);
  const [paying, setPaying] = useState<CommissionStatement | null>(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const valid = /^\d{4}-(0[1-9]|1[0-2])$/.test(period);
  const downloadCsv = async () => {
    setCsvBusy(true);
    try {
      const { blob, fileName } = await api.blob(`/v1/admin/commissions/export.csv?period=${period}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? `komisyon-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'step_up_required'))
        toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setCsvBusy(false);
    }
  };
  const list = useQuery({
    queryKey: ['commissions', period],
    queryFn: () => api.get<CommissionStatement[]>(`/v1/admin/commissions?period=${period}`),
    enabled: valid,
  });
  const calc = useApiMutation(
    () => api.post<{ statements: number; lockedReps: number }>('/v1/admin/commissions/calculate', { period }),
    {
      invalidate: [['commissions']],
      success: (r) => t('admin.commissions.calculated', { count: r.statements, locked: r.lockedReps }),
    },
  );
  const approve = useApiMutation((id: string) => api.post(`/v1/admin/commissions/${id}/approve`, {}), {
    invalidate: [['commissions']],
    success: t('admin.commissions.approved'),
  });
  return (
    <Section
      title={t('admin.commissions.statements')}
      description={t('admin.commissions.statementsLead')}
      action={<HelpTip k="sales_commissions.period" />}
    >
      <div className="flex flex-wrap items-end gap-2">
        <Field label={t('admin.commissions.period')}>
          {(p) => (
            <Input
              {...p}
              type="month"
              className="w-44"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            />
          )}
        </Field>
        {manage && (
          <span className="flex items-center gap-1">
            <Button
              variant="primary"
              loading={calc.isPending}
              disabled={!valid}
              onClick={() => calc.mutate(undefined)}
            >
              {t('admin.commissions.calculate')}
            </Button>
            <HelpTip k="sales_commissions.calculate" />
          </span>
        )}
        {valid && !!list.data?.length && (
          <span className="flex items-center gap-1">
            <Button loading={csvBusy} onClick={() => void downloadCsv()}>
              {t('admin.commissions.downloadCsv')}
            </Button>
            <HelpTip k="sales_commissions.csv" />
          </span>
        )}
      </div>
      {calc.error && !calc.error.errors.length && (
        <p role="alert" className="mt-2 text-sm text-rose">
          {calc.error.title}
        </p>
      )}
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data?.length ? (
        <p className="mt-3 text-sm text-ink-3">{t('admin.commissions.noStatements')}</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[40rem] text-sm" aria-label={t('admin.commissions.statements')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.commissions.rep')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.commissions.orders')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.commissions.base')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.commissions.commission')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.commissions.status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {list.data.map((s) => (
                <tr key={s.id} className="border-t border-line">
                  <td className="px-3 py-2">{s.repName ?? '—'}</td>
                  <td className="px-3 py-2 text-end tabular">{s.totals.reduce((a, x) => a + x.orders, 0)}</td>
                  <td className="px-3 py-2 text-end tabular">
                    {s.totals.map((x) => money(x.baseMinor, x.currency)).join(' · ')}
                  </td>
                  <td className="px-3 py-2 text-end tabular font-medium">
                    {s.totals.map((x) => money(x.commissionMinor, x.currency)).join(' · ')}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={statusTone[s.status]}>{t(`admin.commissions.statuses.${s.status}`)}</Badge>
                    {s.paidNote && <p className="text-xs text-ink-3">{s.paidNote}</p>}
                  </td>
                  <td className="px-3 py-2 text-end">
                    <span className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setOpen(s.id)}>
                        {t('admin.commissions.details')}
                      </Button>
                      {manage && s.status === 'draft' && (
                        <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate(s.id)}>
                          {t('admin.commissions.approve')}
                        </Button>
                      )}
                      {manage && s.status === 'approved' && (
                        <Button size="sm" onClick={() => setPaying(s)}>
                          {t('admin.commissions.markPaid')}
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <StatementDialog id={open} onClose={() => setOpen(null)} />}
      {paying && <PaidDialog statement={paying} onClose={() => setPaying(null)} />}
    </Section>
  );
}

function StatementDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const d = useQuery({
    queryKey: ['commission', id],
    queryFn: () => api.get<CommissionStatementDetail>(`/v1/admin/commissions/${id}`),
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      variant="sheet"
      title={
        d.data
          ? t('admin.commissions.detailTitle', { name: d.data.repName ?? '', period: d.data.period })
          : ''
      }
      closeLabel={t('common.close')}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      {!d.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ink-2">
            {t('admin.commissions.calculatedAt', { date: formatDate(d.data.calculatedAt, true) })}
          </p>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[34rem] text-sm" aria-label={t('admin.commissions.entries')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.orders.number')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.commissions.kind')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.commissions.collected')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.commissions.base')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.commissions.commission')}</th>
                </tr>
              </thead>
              <tbody>
                {d.data.entries.map((e) => (
                  <tr key={e.id} className="border-t border-line">
                    <td className="px-3 py-2">
                      {e.orderId ? (
                        <Link
                          to="/siparisler/$id"
                          params={{ id: e.orderId }}
                          className="text-pine hover:underline"
                        >
                          {e.orderNumber}
                        </Link>
                      ) : (
                        e.orderNumber
                      )}
                    </td>
                    <td className="px-3 py-2">{t(`admin.commissions.kinds.${e.kind}`)}</td>
                    <td className="px-3 py-2 text-end tabular">{bpsLabel(e.collectedBps)}</td>
                    <td className="px-3 py-2 text-end tabular">{money(e.baseMinor, e.currency)}</td>
                    <td className="px-3 py-2 text-end tabular">{money(e.commissionMinor, e.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function PaidDialog({ statement, onClose }: { statement: CommissionStatement; onClose: () => void }) {
  const t = useT();
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const paid = useApiMutation(
    () =>
      api.post(`/v1/admin/commissions/${statement.id}/paid`, { note: note.trim() }, { idempotencyKey: idem }),
    {
      invalidate: [['commissions']],
      success: t('admin.commissions.paidDone'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.commissions.markPaidTitle', { name: statement.repName ?? '' })}
      description={t('admin.commissions.markPaidLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={paid.isPending}
            disabled={note.trim().length < 3}
            onClick={() => paid.mutate(undefined)}
          >
            {t('admin.commissions.markPaid')}
          </Button>
        </>
      }
    >
      <Field
        label={t('admin.commissions.paidNote')}
        required
        help={<HelpTip k="sales_commissions.paid_note" />}
        error={fieldError(paid.error, 'note')}
      >
        {(p) => (
          <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}
